import type {
  CustomFieldDefinition,
  DataExtractionFieldDefinitionPostPayload,
} from "@repo/app-types/entities/customField";
import {
  buildListTag,
  getTagsInvalidatedByCreate,
  getTagsInvalidatedByDelete,
  getTagsInvalidatedByUpdate,
  getTagsProvidedByList,
  TagType,
} from "../helpers/tags.js";
import { api } from "../redux.js";

const dataExtractionFieldApi = api.injectEndpoints({
  endpoints: (builder) => ({
    allDataExtractionFieldDefinitions: builder.query<
      CustomFieldDefinition[],
      void
    >({
      query: () => `/custom_fields/data-extraction-field-definitions`,
      providesTags: (result) =>
        getTagsProvidedByList(TagType.DataExtractionFieldDefinition, result),
    }),
    createDataExtractionFieldDefinition: builder.mutation<
      CustomFieldDefinition,
      DataExtractionFieldDefinitionPostPayload
    >({
      query: (values) => ({
        method: "POST",
        url: `/custom_fields/data-extraction-field-definitions`,
        body: values,
      }),
      invalidatesTags: () =>
        getTagsInvalidatedByCreate(TagType.DataExtractionFieldDefinition),
    }),
    updateDataExtractionFieldDefinition: builder.mutation<
      CustomFieldDefinition,
      { id: string; display_name: string; description?: string | null },
      { data: { field: CustomFieldDefinition } }
    >({
      query: ({ id, ...body }) => ({
        method: "PATCH",
        url: `/custom_fields/data-extraction-field-definitions/${encodeURIComponent(id)}`,
        body,
      }),
      transformResponse: (response) => response.data.field,
      invalidatesTags: (_result, _error, { id }) =>
        getTagsInvalidatedByUpdate(TagType.DataExtractionFieldDefinition, id),
    }),
    deleteDataExtractionFieldDefinition: builder.mutation<void, string>({
      query: (id) => ({
        method: "DELETE",
        url: `/custom_fields/data-extraction-field-definitions/${encodeURIComponent(id)}`,
      }),
      onQueryStarted: async (id, { dispatch, queryFulfilled }) => {
        const patch = dispatch(
          dataExtractionFieldApi.util.updateQueryData(
            "allDataExtractionFieldDefinitions",
            undefined,
            (draft) => {
              const index = draft.findIndex(
                (definition) => definition.id === id,
              );
              if (index !== -1) {
                draft.splice(index, 1);
              }
            },
          ),
        );
        try {
          await queryFulfilled;
        } catch {
          patch.undo();
        }
      },
      invalidatesTags: (result, error, id) =>
        getTagsInvalidatedByDelete(TagType.DataExtractionFieldDefinition, id),
    }),
    dataExtractionFieldDefinitionsByJobTypeId: builder.query<
      string[],
      { jobTypeId: string },
      { data: { data_extraction_field_definition_ids: string[] } }
    >({
      query: ({ jobTypeId }) =>
        `/job-types/${encodeURIComponent(jobTypeId)}/data-extraction-field-definitions`,
      transformResponse: (response) =>
        response.data.data_extraction_field_definition_ids,
      // We want this query to be invalidated when:
      // - the list of IDs for a job type is itself invalidated.
      // - any of the field definitions referenced by ID are deleted.
      providesTags: (result, error, { jobTypeId }) =>
        getTagsProvidedByList(TagType.DataExtractionFieldDefinition, result, {
          filteredBy: [jobTypeId],
        }),
    }),
    updateDataExtractionFieldDefinitionsForJobType: builder.mutation<
      string[],
      { jobTypeId: string; customFieldDefinitionIds: string[] },
      { data: { data_extraction_field_definition_ids: string[] } }
    >({
      query: ({ jobTypeId, customFieldDefinitionIds }) => ({
        method: "PUT",
        url: `/job-types/${encodeURIComponent(jobTypeId)}/data-extraction-field-definitions`,
        body: {
          custom_field_definition_ids: customFieldDefinitionIds,
        },
      }),
      transformResponse: (response) =>
        response.data.data_extraction_field_definition_ids,
      // Also invalidate the unfiltered list because the server may have
      // materialized a system-managed field (e.g. "Next interview questions")
      // as part of resolving sentinel ids in the request.
      invalidatesTags: (result, error, { jobTypeId }) => [
        buildListTag(TagType.DataExtractionFieldDefinition, [jobTypeId]),
        buildListTag(TagType.DataExtractionFieldDefinition, []),
      ],
    }),
  }),
});

export const {
  useAllDataExtractionFieldDefinitionsQuery,
  useCreateDataExtractionFieldDefinitionMutation,
  useUpdateDataExtractionFieldDefinitionMutation,
  useDeleteDataExtractionFieldDefinitionMutation,
  useDataExtractionFieldDefinitionsByJobTypeIdQuery,
  useUpdateDataExtractionFieldDefinitionsForJobTypeMutation,
} = dataExtractionFieldApi;
